import React, { useState, useEffect, useCallback } from "react";
import validator from '@rjsf/validator-ajv8';
import Form from '@rjsf/core';
import { RJSFSchema, UiSchema, WidgetProps } from '@rjsf/utils';
import { generateUISchema } from "./SchemaFormTemplates/generateUISchema";
import HorizontalFieldTemplate from "./SchemaFormTemplates/HorizontalFieldTemplate";
import CustomDescriptionField from "./SchemaFormTemplates/CustomDescriptionField";
import { useTranslation } from "react-i18next";

// Define the types for SchemaFormProps
type SchemaFormProps = {
  userIsAdmin: boolean,
  formTitle: string,
  schema: RJSFSchema,
  ui_schema: UiSchema,
  formdata?: Object,
  onChange: Function
  isEditable: boolean,
  errors?: any
}

const SchemaForm = ({
  userIsAdmin,
  formTitle,
  schema,
  ui_schema,
  formdata,
  onChange,
  isEditable,
  errors
}: SchemaFormProps) => {
  const { t } = useTranslation();
  const [visiblePasswords, setVisiblePasswords] = useState<Record<string, boolean>>({});
  const [customSchema, setCustomSchema] = useState<UiSchema>({});
  const [formData, setFormData] = useState(formdata ? formdata : null);
  const [loading, setLoading] = useState(true); // Add loading state
  const printError = (error: any) => console.error("schema form error", { error });

  // Create a toggle password function that takes the field name as a parameter
  // We use useCallback with empty deps to ensure this function never changes its reference
  const togglePassword = useCallback((fieldName: string) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  }, []);

  // Store a reference to togglePassword that can be safely used in dependency arrays
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableTogglePassword = React.useMemo(() => togglePassword, []);

  // Initial setup and when dependencies change
  useEffect(() => {
    if (formData) {
      setCustomSchema(generateUISchema(userIsAdmin, schema, ui_schema, errors, visiblePasswords, stableTogglePassword, formData));
    }
  }, [userIsAdmin, schema, ui_schema, errors, formData, visiblePasswords, stableTogglePassword]);

  // Initialize form data from props
  useEffect(() => {
    setFormData(formdata || {});
  }, [formdata]);

  const handleFormChange = (newFormData: any) => {
    setFormData(newFormData); // Update form data without regenerating UI schema on every change
  };

  return (
    <div className="flex flex-col w-full" id="json-form">
      {/* Display root level description below the form title if it exists, preferring ui_schema['ui:description'] */}
      {(ui_schema && ui_schema['ui:description']) ? (
        <div className="form-root-description text-gray-600 dark:text-gray-300 mt-1 mb-3 transition-colors duration-300">
          {ui_schema['ui:description']}
        </div>
      ) : schema.description ? (
        <div className="form-root-description text-gray-600 dark:text-gray-300 mt-1 mb-3 transition-colors duration-300">
          {schema.description}
        </div>
      ) : null}
      <div className="custom-form w-full dark:bg-gray-800 rounded transition-all duration-300">
        <Form
          key={JSON.stringify(customSchema)}
          schema={schema}
          uiSchema={{
            ...customSchema,
            // Hide the root level description in the form since we're showing it above
            'ui:description': ''
          }}
          validator={validator}
          onChange={(e) => {
            handleFormChange(e.formData)
            onChange(e.formData)
          }}
          onError={printError}
          children={true}
          formData={formData}
          disabled={!isEditable}
          className="rjsf-form-tailwind dark:bg-gray-800 transition-all duration-300"
          templates={{
            FieldTemplate: HorizontalFieldTemplate,
            DescriptionFieldTemplate: CustomDescriptionField
          }}
        />
      </div>
    </div>
  )
}

export default SchemaForm;
